import React, { useCallback, useMemo } from 'react';

import { Stack } from '@mui/material';

import { ConflictResolution, ExistingBlackoutDate, ManualRoomDate } from '~/types/stopsell';

import { findRateConflict } from '~/utils/stopsellConflicts';

import EditableDateRow from './EditableDateRow';
import StopsellRateRow from './StopsellRateRow';

interface Props {
  date: ManualRoomDate;
  dateIndex: number;
  roomIndex: number;
  catalogRoomRates: Array<{ id: string; name: string }>;
  blackoutDates: Array<ExistingBlackoutDate>;
  rateResolutions: Record<string, ConflictResolution>;
  onToggle: (dateIndex: number, selected: boolean) => void;
  onEdit: (dateId: string, newRange: { start: string; end: string }) => void;
  onDelete: (dateIndex: number) => void;
  onResolve: (roomIndex: number, dateId: string, rateId: string, status: ConflictResolution) => void;
  onUndo: (roomIndex: number, dateId: string, rateId: string) => void;
}

export default function StopsellManualDateRow({
  date,
  dateIndex,
  roomIndex,
  catalogRoomRates,
  blackoutDates,
  rateResolutions,
  onToggle,
  onEdit,
  onDelete,
  onResolve,
  onUndo,
}: Props) {
  const rateConflicts = useMemo(
    () => catalogRoomRates.map((rate) => findRateConflict(date, rate.id, blackoutDates)),
    [catalogRoomRates, date, blackoutDates],
  );

  const handleToggle = useCallback((v: boolean) => onToggle(dateIndex, v), [onToggle, dateIndex]);
  const handleEdit = useCallback((range: { start: string; end: string }) => onEdit(date.id, range), [onEdit, date.id]);
  const handleDelete = useCallback(() => onDelete(dateIndex), [onDelete, dateIndex]);

  return (
    <EditableDateRow
      start={date.start}
      end={date.end}
      selected={date.selected}
      onToggle={handleToggle}
      onEdit={handleEdit}
      onDelete={handleDelete}
      chipSx={{ bgcolor: 'warning.50', color: 'warning.dark', fontSize: 11 }}
    >
      {catalogRoomRates.length > 0 && date.selected && (
        <Stack gap={0.5} sx={{ pl: 4 }}>
          {catalogRoomRates.map((rate, rateIndex) => {
            const conflict = rateConflicts[rateIndex] ?? { status: 'clear' as const };
            const resolution = rateResolutions[`${date.id}-${rate.id}`] ?? null;
            return (
              <StopsellRateRow
                key={rate.id}
                rateName={rate.name}
                rateConflict={conflict}
                resolution={resolution}
                onResolve={(status) => onResolve(roomIndex, date.id, rate.id, status)}
                onUndo={() => onUndo(roomIndex, date.id, rate.id)}
              />
            );
          })}
        </Stack>
      )}
    </EditableDateRow>
  );
}
